import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import type { RfqHubFileRelease, RfqPackage } from '../../types';
import {
  normalizeRfqReleaseDisplayLabel,
  rfqPackageIdsForHubRelease,
} from '../../utils/rfqHubReleaseDisplayLabel';
import { updateRfqHubReleaseSupplierDisplayLabel } from '../../utils/rfqHubFileReleaseService';

export interface RfqReleaseDisplayLabelEditorProps {
  release: RfqHubFileRelease;
  packages: RfqPackage[];
  isPublished: boolean;
}

const RfqReleaseDisplayLabelEditor: React.FC<RfqReleaseDisplayLabelEditorProps> = ({
  release,
  packages,
  isPublished,
}) => {
  const releaseId = release.id?.trim() || '';
  const rfqProjectId = release.rfqProjectId.trim();
  const fallback = release.fileName || 'Release';
  const storedLabel = (release.displayLabel || '').trim();
  const shownLabel = storedLabel || fallback;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shownLabel);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(shownLabel);
  }, [editing, shownLabel]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const cancelEdit = useCallback(() => {
    setDraft(shownLabel);
    setEditing(false);
  }, [shownLabel]);

  const commitEdit = useCallback(async () => {
    if (!releaseId || !rfqProjectId || saving) return;
    const next = normalizeRfqReleaseDisplayLabel(draft, fallback);
    if (next === shownLabel) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const packageIds = rfqPackageIdsForHubRelease(packages, rfqProjectId, releaseId);
      await updateRfqHubReleaseSupplierDisplayLabel({
        rfqProjectId,
        releaseId,
        displayLabel: next,
        publishedPackageIds: packageIds,
      });
      setEditing(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save release name.');
    } finally {
      setSaving(false);
    }
  }, [
    draft,
    fallback,
    packages,
    releaseId,
    rfqProjectId,
    saving,
    shownLabel,
  ]);

  if (!releaseId) return <span className="srfq-timber-version-label">{shownLabel}</span>;

  if (editing) {
    return (
      <span
        className="srfq-release-label-edit"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          className="srfq-release-label-edit-input"
          value={draft}
          disabled={saving}
          aria-label="Supplier-facing release name"
          title={isPublished ? 'Name shown to suppliers on their RFQ portal' : 'Name shown to suppliers when published'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitEdit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
          onBlur={() => {
            void commitEdit();
          }}
        />
        {saving ? <Loader2 size={12} className="srfq-timber-spin" aria-hidden /> : null}
      </span>
    );
  }

  return (
    <span className="srfq-release-label-edit srfq-release-label-edit--display">
      <span className="srfq-timber-version-label">{shownLabel}</span>
      <button
        type="button"
        className="srfq-release-label-edit-btn"
        aria-label={`Edit supplier-facing name (${shownLabel})`}
        title={
          isPublished
            ? 'Edit name suppliers see on their RFQ portal'
            : 'Edit name suppliers will see when you publish'
        }
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setEditing(true);
        }}
      >
        <Pencil size={11} aria-hidden />
      </button>
    </span>
  );
};

export default RfqReleaseDisplayLabelEditor;
